import {Component, computed, HostListener, inject, Injector, signal, ChangeDetectionStrategy} from '@angular/core';
import {Router} from '@angular/router';
import {FlexLayoutModule, FlexModule} from "@ngbracket/ngx-layout";
import {
  ActionType,
  CardComponent,
  DimmerComponent,
  listenForKeyboardCode,
  MaintenanceComponent,
  screenSize,
  TemplateForDirective,
} from "@mindpath/shared";
import {config} from '../config';
import {BookingPage, FinderPage, NavService, Section} from "./services/nav.service";
import {PageViewComponent} from "./Views/page-view/page-view.component";
import {EventsDebuggerComponent} from "./analytics/events-debugger/events-debugger.component";
import {TraceClass} from "@sentry/angular";
import {AnalyticsService} from "./analytics/analytics.service";
import {AppService} from "./services/app.service";
import {AuthService} from "./auth";
import {UiService} from './services/ui.service';
import {assertExists} from "./assert";
import {InitializeApp} from "./app.init";
import {AppErrorAction} from "../lib/shared/errors/app-error";
import {ReferralSourceService} from "./services/referral-source.service";
import {MessageTextComponent} from "./components/message-text/message-text.component";


@Component({
  selector: 'app-root',
  imports: [FlexModule, TemplateForDirective,
    CardComponent,
    MessageTextComponent,
    MaintenanceComponent,
    DimmerComponent, FlexLayoutModule, PageViewComponent, EventsDebuggerComponent],

  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
@TraceClass()
export class AppComponent {

  analytics = inject(AnalyticsService);

  router = inject(Router);

  dimmerMsg = computed(() => {
    const dimmer = this.ui.dimming();
    const isStopMsg = dimmer.asStopMessage;
    const title = dimmer.title;
    const message = dimmer.message;

    if (isStopMsg) {
      return {title: undefined, message: undefined};
    }
    return {title, message};
  });

  protected readonly screenSize = screenSize;
  protected readonly ActionType = ActionType;

  protected readonly navSvc = inject(NavService);

  #injector = inject(Injector);
  constructor() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).events = () => {
      this.debuggerOpen.update(v => !v);
    }
    if (!config.inMaintenance()) {

      this.ui.showAppLoading();
      InitializeApp(this.#injector)
        .finally(() => {
          setTimeout(() => {
            this.ui.hideLoading({onlyIfAppLoading: true, ignoreError: false});
          }, 250);
        });
    }
  }

  ui = inject(UiService);
  app = inject(AppService);
  auth = inject(AuthService);
  nav = inject(NavService);
  referral = inject(ReferralSourceService);

  debuggerOpen = signal(false);

  @HostListener('document:keyup', ['$event'])
  listenForMaintenanceBackdoor = listenForKeyboardCode('--letmein', () => {
    config.inMaintenance.set(false);
  });
  protected readonly config = config;

  // Listen for click events on the document to check for external link clicks
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = (event.target as HTMLElement).closest('a');
    if (target && target.href && !target.href.startsWith(window.location.origin)) {
      if (!this.promptLeaveConfirm(event)) {
        event.preventDefault();
      }
    }
  }

  // Listen for history navigation events (back/forward buttons)
  @HostListener('window:popstate')
  onPopState() {

    // if (!this.promptLeaveConfirm(event)) {
    //   event.preventDefault();
    // }
  }

  promptLeaveConfirm(event: Event|null|undefined) {

    const target = event?.target;
    if (target && target instanceof HTMLElement) {
      // if the back button is clicked or the link is internal, allow the navigation
      if (target.id === 'back-btn' || target instanceof HTMLAnchorElement && target.href.startsWith(window.location.origin)) {
        return true;
      }
    }

    const {section} = this.nav.current();
    if (section !== Section.Finder && section !== Section.Booking) {
      return true;
    }
    return confirm('Leaving this page will discard the information entered. Leave anyway?');
  }


  async handleErrorAction(action: AppErrorAction) {
    switch (action) {
      case AppErrorAction.ClearError:
        this.app.clearError();
        this.ui.hideStopMessage();
        break;
      case AppErrorAction.BackToFinder:
        this.ui.hideStopMessage();
        await this.nav.goto(Section.Finder, FinderPage.Find, {replaceUrl: true});
        break;
      case AppErrorAction.BackToOtp:
        this.ui.hideStopMessage();
        // Scheduling on behalf of the patient has no code step; contact info is where the intake starts.
        await this.nav.goto(Section.Booking, this.referral.schedulingOnBehalfOf() ? BookingPage.ContactInfo : BookingPage.Verification);
        break;
      case AppErrorAction.BackToContactInfo:
        this.ui.hideStopMessage();
        await this.nav.goto(Section.Booking, BookingPage.ContactInfo);
        break;
      case AppErrorAction.HistoryBack:
        history.back();
        break;
      case AppErrorAction.RequestNewOtp:
        if (this.referral.schedulingOnBehalfOf()) {
          this.ui.hideStopMessage();
          await this.nav.goto(Section.Booking, BookingPage.ContactInfo);
          break;
        }
        try {
          const demographics = this.app.data().demographics;
          assertExists(demographics);
          this.ui.showLoading('Sending Code', 'Sending a verification code');
          await this.auth.reRequestAppointmentOtp(demographics);
        } catch (e) {
          this.app.errorOccurred(e, {
            devDetails: {
              message: 'Error occurred while requesting a new OTP',
            }
          });
        } finally {
          this.ui.hideLoading({ignoreError: false});
        }
        break;
      default: {
        this.app.errorOccurred(new Error(`Unhandled error action: \`${action}\``), {
          devDetails: {
            message: `Unhandled error action: \`${action}\``,
            context: {
              action
            }
          }
        });
      }

    }

  }
}
